import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { api, type BelegAktion, type BelegFeld, type BelegRegel } from "../api";
import { Select } from "./Select";
import { bestaetigen, fehlerText, toast } from "./verwaltung";

export const AKTION_LABEL: Record<BelegAktion, { label: string; hint: string }> = {
  ignorieren: { label: "Nie als Beleg", hint: "Wird gar nicht erst abgelegt — z. B. Shop-Bestellungen für Kunden." },
  unterlage: { label: "Nur als Unterlage", hint: "Wird abgelegt, zählt aber nie — z. B. Auswertungen und Lohn vom Steuerbüro." },
  immer: { label: "Immer als Beleg", hint: "Wird auch eingesammelt, wenn die Mail nicht nach Rechnung aussieht." },
};

const FELD_LABEL: Record<BelegFeld, string> = {
  absender: "Absender ist",
  domain: "Absender-Domain ist",
  betreff: "Betreff enthält",
};

/**
 * Eigene Regeln für Belege und die Namen deiner Firma. Getrennt von den
 * Mailregeln: Hier geht es nur darum, was in die Voranmeldung gehört.
 */
export function BelegRegelnTab({ onGeaendert }: { onGeaendert: () => void }) {
  const [regeln, setRegeln] = useState<BelegRegel[] | null>(null);
  const [namen, setNamen] = useState("");
  const [namenGesetzt, setNamenGesetzt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState<{ feld: BelegFeld; wert: string; aktion: BelegAktion }>({ feld: "absender", wert: "", aktion: "unterlage" });
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      const r = await api.belegRegeln();
      setRegeln(r.regeln);
      setNamen(r.eigeneNamen.join("\n"));
      setNamenGesetzt(r.eigeneNamenGesetzt);
      setFehler(null);
    } catch (e) {
      setFehler(fehlerText(e));
    }
  }, []);
  useEffect(() => { void laden(); }, [laden]);

  const anlegen = async () => {
    if (neu.wert.trim().length < 2) { toast.fehler("Bitte einen Wert eintragen."); return; }
    setBusy(true);
    try {
      const r = await api.belegRegelAnlegen(neu.feld, neu.wert.trim(), neu.aktion);
      toast.erfolg(r.angewendet > 0
        ? `Regel angelegt — ${r.angewendet} vorhandene ${r.angewendet === 1 ? "Beleg" : "Belege"} neu eingeordnet.`
        : "Regel angelegt — greift ab jetzt bei neuen Mails.");
      setNeu((n) => ({ ...n, wert: "" }));
      await laden();
      onGeaendert();
    } catch (e) {
      toast.fehler(e);
    } finally {
      setBusy(false);
    }
  };

  const loeschen = async (r: BelegRegel) => {
    const ok = await bestaetigen({
      titel: "Regel löschen?",
      text: <>Bereits eingeordnete Belege bleiben, wie sie sind. Neue Mails von „{r.wert}" werden wieder normal geprüft.</>,
      bestaetigen: "Löschen",
      gefahr: true,
    });
    if (!ok) return;
    try {
      await api.belegRegelLoeschen(r.id);
      await laden();
    } catch (e) {
      toast.fehler(e);
    }
  };

  const namenSpeichern = async () => {
    setBusy(true);
    try {
      const liste = namen.split(/\n|,/).map((n) => n.trim()).filter(Boolean);
      const r = await api.eigeneNamenSetzen(liste);
      setNamen(r.eigeneNamen.join("\n"));
      setNamenGesetzt(true);
      toast.erfolg("Firmennamen gespeichert — gilt für neue Belege.");
    } catch (e) {
      toast.fehler(e);
    } finally {
      setBusy(false);
    }
  };

  if (fehler && !regeln) {
    return (
      <div className="page"><div className="v-leer" data-art="fehler">
        <strong>Regeln ließen sich nicht laden</strong><span>{fehler}</span>
        <button className="btn" onClick={() => void laden()}>Erneut versuchen</button>
      </div></div>
    );
  }
  if (!regeln) return <div className="v-laden" role="status">Lade Regeln …</div>;

  return (
    <div className="page">
      <h2>Deine Firma</h2>
      <p className="lede">
        Unter diesen Namen gehen Rechnungen an dich. Steht auf einer Rechnung ein anderer Empfänger — etwa die
        Shop-Bestellung eines Kunden —, landet sie als „Nicht an dich" und zählt nicht. Ein Name pro Zeile.
      </p>
      <div className="beleg-namen">
        <textarea
          className="input"
          rows={4}
          value={namen}
          onChange={(e) => setNamen(e.target.value)}
          placeholder={"KreativWolke\nManuel Grasse Webdesign"}
          aria-label="Eigene Firmennamen"
        />
        <div className="beleg-namen-fuss">
          <span className="hint" style={{ margin: 0 }}>
            {namenGesetzt ? "Von dir festgelegt." : "Vorschlag aus Kontoname und Maildomain — bitte prüfen und speichern."}
          </span>
          <button className="btn primary" onClick={() => void namenSpeichern()} disabled={busy}>Speichern</button>
        </div>
      </div>

      <h2 style={{ marginTop: 32 }}>Regeln für Belege</h2>
      <p className="lede">
        Entscheiden, was aus einer Mail in die Belege kommt — unabhängig von den Mailregeln. Neue Regeln ordnen
        auch vorhandene Belege um (außer denen, die du schon geprüft hast).
      </p>

      <div className="beleg-regel-neu">
        <Select
          value={neu.feld}
          onChange={(v) => setNeu((n) => ({ ...n, feld: v as BelegFeld }))}
          options={(Object.keys(FELD_LABEL) as BelegFeld[]).map((f) => ({ value: f, label: FELD_LABEL[f] }))}
          aria-label="Bedingung"
          searchable={false}
        />
        <input
          className="input"
          value={neu.wert}
          onChange={(e) => setNeu((n) => ({ ...n, wert: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter") void anlegen(); }}
          placeholder={neu.feld === "absender" ? "steuer@kanzlei.de" : neu.feld === "domain" ? "mr-window.de" : "Lohnunterlagen"}
          aria-label="Wert"
        />
        <Select
          value={neu.aktion}
          onChange={(v) => setNeu((n) => ({ ...n, aktion: v as BelegAktion }))}
          options={(Object.keys(AKTION_LABEL) as BelegAktion[]).map((a) => ({ value: a, label: AKTION_LABEL[a].label, hint: AKTION_LABEL[a].hint }))}
          aria-label="Aktion"
          searchable={false}
        />
        <button className="btn primary" onClick={() => void anlegen()} disabled={busy}>
          <Plus aria-hidden="true" /> Anlegen
        </button>
      </div>

      {regeln.length === 0 ? (
        <p className="hint">
          Noch keine Regeln. Schneller geht es aus einem Beleg heraus: Beleg öffnen → „Absender nur als Unterlage".
        </p>
      ) : (
        <div className="v-tabelle">
          <table className="log">
            <thead><tr><th>Bedingung</th><th>Aktion</th><th><span className="v-sr">Löschen</span></th></tr></thead>
            <tbody>
              {regeln.map((r) => (
                <tr key={r.id}>
                  <td>{FELD_LABEL[r.feld]} <b className="mono">{r.wert}</b></td>
                  <td><span className="v-status" data-status={r.aktion === "immer" ? "geprueft" : r.aktion === "unterlage" ? "unterlage" : "ignoriert"}>{AKTION_LABEL[r.aktion].label}</span></td>
                  <td className="v-aktion">
                    <button className="icon-btn gefahr" onClick={() => void loeschen(r)} aria-label={`Regel ${r.wert} löschen`} title="Regel löschen">
                      <Trash2 aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
